/**
 * Agent Run Schemas — the vocabularies and wire shape for `AgentRun`.
 *
 * `AgentRun` is the ledger row for every agent run, however it started: a
 * human typing `/bug`, a Linear delegation, a Granola meeting note, a Slack
 * mention, or one agent spawning another. Steps inside a run land as
 * `OperationsEvent` rows (`domain: 'agent'`, `eventClass: 'agent_run'`) keyed
 * by `correlationId = run_<publicId>`, not a second child table.
 *
 * ⚠️ **`reportTokenHash` IS NOT ON THE WIRE SHAPE AND MUST NEVER BE ADDED.**
 * It is the sha256 of the per-run report token, minted once by `claim` (U2)
 * and never re-derivable to the plaintext. Serializing the hash onto any read
 * surface would hand an attacker who compromises a read path a fixed target
 * to brute-force offline — the same discipline that keeps `rawPayload` off
 * `WebhookDeliverySchema` (see that file). The plaintext token itself is
 * returned exactly once, by the claim response, and lives nowhere else.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-29-001-feat-agent-run-ledger-event-driven-runs-plan.md
 */

import { z } from 'zod'

// =============================================================================
// Lanes
// =============================================================================

/**
 * Which skill runs. Closed on purpose: the dispatcher, the classifier rubric,
 * and the per-lane read surfaces all key on this string, so a lane added by a
 * typo would spawn a run nothing routes and nothing shows.
 *
 * `investigate` is the factory's read-only research lane: it ends with an
 * evidence packet in `outcomes.research` ({@link ResearchPacketSchema}), not a
 * PR, and runs only through its phase list (`tooling/coordinator/lanes/`).
 *
 * `kleos` is the one lane no skill owns: it holds work handed over by a KLEOS
 * agent through the facade router, and exists as its own lane so that
 * `@@unique([lane, fingerprint])` reads exactly "one run per KLEOS client
 * key". Only the facade may create runs in it — the shared-key ledger route
 * refuses `lane: 'kleos'` on create, because anything holding that key could
 * otherwise drive a forged run terminal and have the completion hook sign a
 * `run.completed` into a partner's connection.
 */
export const AGENT_RUN_LANES = [
  'defect',
  'improvement',
  'feature',
  'investigate',
  'meeting',
  'linear-agent',
  'kleos'
] as const

/**
 * The canonical lane names only, with no legacy alias. Record KEYS use this
 * (config maps normalize their keys first, {@link canonicalLaneKeys}); every
 * VALUE read goes through {@link AgentRunLaneSchema}, which accepts the aliases.
 */
export const AgentRunLaneNameSchema = z.enum(AGENT_RUN_LANES)
export type AgentRunLane = z.infer<typeof AgentRunLaneNameSchema>

/**
 * The lane names retired by SCH-2309, when the factory lanes took the Linear
 * Type label names, and the lane each one now means. THE ONE PLACE the old
 * names live.
 *
 * They are not gone: production `AgentRun.lane`, `classification` and
 * `classOverride` hold them, `factory.*` config rows may be keyed by them, VMs
 * on older images post them to the ledger, and the retiring `/bug`, `/improve`
 * and `/ship-feature` skills pass them to the CLIs. Every lane schema below
 * accepts them and outputs the new name, so nothing new is ever WRITTEN under
 * an old name; a query that must still FIND old rows filters on
 * {@link storedLaneNames}.
 */
export const LEGACY_LANE_ALIASES = {
  bug: 'defect',
  improve: 'improvement',
  ship: 'feature'
} as const satisfies Readonly<Record<string, AgentRunLane>>
export type LegacyAgentRunLane = keyof typeof LEGACY_LANE_ALIASES

const LEGACY_LANES = Object.keys(LEGACY_LANE_ALIASES) as LegacyAgentRunLane[]

/** A legacy lane name becomes its new name; anything else comes back unchanged. */
export function normalizeLane(value: string): string
export function normalizeLane(value: unknown): unknown
export function normalizeLane(value: unknown): unknown {
  return typeof value === 'string' && Object.hasOwn(LEGACY_LANE_ALIASES, value)
    ? LEGACY_LANE_ALIASES[value as LegacyAgentRunLane]
    : value
}

/**
 * Every spelling a lane may be STORED under: the lane itself, then each legacy
 * name that aliases to it. A Prisma filter on `lane: 'defect'` alone would miss
 * every row written as `bug`; `{ in: storedLaneNames('defect') }` finds both.
 */
export function storedLaneNames(lane: string): string[] {
  return [lane, ...LEGACY_LANES.filter(legacy => LEGACY_LANE_ALIASES[legacy] === lane)]
}

/**
 * A lane-keyed map with its legacy keys renamed. When both spellings are
 * present the new key wins, so a row reseeded under the new name overrides the
 * leftover old entry; an old key alone is read as its new one. Read-side only:
 * nothing here writes a row back.
 */
export function canonicalLaneKeys(value: unknown): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value
  const out: Record<string, unknown> = { ...(value as Record<string, unknown>) }
  for (const legacy of LEGACY_LANES) {
    if (!Object.hasOwn(out, legacy)) continue
    const lane = LEGACY_LANE_ALIASES[legacy]
    if (!Object.hasOwn(out, lane)) out[lane] = out[legacy]
    delete out[legacy]
  }
  return out
}

/**
 * A closed lane list that also accepts the legacy names of its members and
 * outputs the new ones. `z.preprocess` rather than a union with a transform:
 * the JSON Schema of a preprocess is the inner enum in both io modes, so a
 * model or envelope schema built from it still advertises only the new names.
 */
export function laneEnumWithLegacyAliases<const T extends readonly [string, ...string[]]>(lanes: T) {
  return z.preprocess(normalizeLane, z.enum(lanes))
}

export const AgentRunLaneSchema = laneEnumWithLegacyAliases(AGENT_RUN_LANES)

/** Canonical names only: pass a stored value through {@link normalizeLane} first. */
export function isAgentRunLane(value: string): value is AgentRunLane {
  return (AGENT_RUN_LANES as readonly string[]).includes(value)
}

// =============================================================================
// Classification — the lane verdict, size and risk (phase runner, KTD24)
// =============================================================================

/**
 * The lanes assessment may write to `AgentRun.classification` and
 * `classOverride`: the factory's four, never another producer's lane.
 *
 * THE LANE NAME ONLY, AND AT MOST 32 CHARACTERS (asserted in this package's
 * test). The column is `VarChar(64)`; a composite verdict would overflow it,
 * PlanetScale strict mode would reject the write, the status CAS would report
 * `count: 0`, and the tick would re-assess the run every minute. Size, risk and
 * evidence live in the `assessment` record and the `run.classified` payload
 * instead (`FactoryClassificationSchema`).
 *
 * `AgentRunSchema.classification` stays a free string on purpose: rows written
 * before the factory (the meeting lane's `customer`) are still read through it.
 */
export const AGENT_RUN_LANE_VERDICTS = [
  'defect',
  'improvement',
  'feature',
  'investigate'
] as const satisfies readonly AgentRunLane[]

/** Accepts a legacy verdict (`bug`, `improve`, `ship`) still stored on older rows. */
export const AgentRunLaneVerdictSchema = laneEnumWithLegacyAliases(AGENT_RUN_LANE_VERDICTS)
export type AgentRunLaneVerdict = z.infer<typeof AgentRunLaneVerdictSchema>

/**
 * How much ceremony a candidate needs — the classifier's hypothesis, which the
 * post-triage re-check may only RAISE (KTD20): a `TINY` guess against a
 * multi-package triage continues as `PLAN`; a `PLAN` guess against a one-file
 * triage parks for a human.
 */
export const SIZE_CLASSES = ['TINY', 'PLAN', 'BRAINSTORM'] as const

export const SizeClassSchema = z.enum(SIZE_CLASSES)
export type SizeClass = z.infer<typeof SizeClassSchema>

/** What a candidate might touch that raises the stakes of getting it wrong. */
export const RISK_FLAGS = [
  'schema',
  'workflow',
  'auth',
  'production-data',
  'customer-data',
  'release',
  'external-write'
] as const

export const RiskFlagSchema = z.enum(RISK_FLAGS)
export type RiskFlag = z.infer<typeof RiskFlagSchema>

/** A set of risk flags: each at most once, so the list is bounded by the vocabulary. */
export const RiskFlagsSchema = z
  .array(RiskFlagSchema)
  .max(RISK_FLAGS.length)
  .refine(flags => new Set(flags).size === flags.length, { message: 'each risk flag at most once' })

// =============================================================================
// Research outcome — the investigate lane's evidence packet (factory operator plan, KTD17)
// =============================================================================

/**
 * What an `investigate` run produces instead of a PR: the question, the cited
 * evidence, the finding, a confidence and the recommended next step. It is the
 * `research` phase's envelope and, once the run completes, `outcomes.research`
 * on the row.
 *
 * A CITATION IS A STRING HERE, NOT A CLOSED FORM, on purpose. The evidence gate
 * (`tooling/coordinator/gates/evidence.ts`) classifies each one — a repo path
 * with a line range, or a `pnpm sj` verb — and refuses anything else by name
 * without running it. A schema that rejected raw SQL would send it back as a
 * shape error and the refusal would never reach the ledger.
 *
 * BOUNDED TO FIT ONE PHASE EVENT. The envelope rides the research step's done
 * event, whose serialized payload is capped at `PHASE_EVENT_PAYLOAD_MAX_CHARS`
 * (12,288); ten fully used citations plus the text fields stay under it with
 * room for the event's own fields (asserted in this package's test).
 */
const RESEARCH_EVIDENCE_MAX = 10

export const ResearchPacketSchema = z.object({
  /** The question the run answered, restated from the brief. */
  question: z.string().min(1).max(500),
  evidence: z
    .array(
      z.object({
        /** `path/to/file.ts:12-40` or `pnpm sj <verb> [args]`. */
        citation: z.string().min(1).max(200),
        /** What this citation shows. */
        claim: z.string().min(1).max(300),
        /** Optional quote from the cited lines; when given, the gate checks it is there. */
        excerpt: z
          .string()
          .min(1)
          .max(160)
          // Whitespace-only would match every line once the gate collapses whitespace.
          .refine(text => text.trim().length > 0, { message: 'an excerpt must quote something' })
          .optional()
      })
    )
    .min(1)
    .max(RESEARCH_EVIDENCE_MAX),
  finding: z.string().min(1).max(1500),
  confidence: z.enum(['high', 'medium', 'low']),
  nextStep: z.string().min(1).max(500)
})
export type ResearchPacket = z.infer<typeof ResearchPacketSchema>

// =============================================================================
// Sources
// =============================================================================

/**
 * Where the inbound event came from. Shared vocabulary with
 * `WebhookDeliveryProvider` for `linear` / `granola` / `slack` — those three
 * are also webhook providers; `granola-sweep`, `manual`, and `agent` never
 * arrive over a webhook and so have no `WebhookDelivery` row at all.
 *
 * `linear-label` is the FACTORY's intake, and it is a separate value from
 * `linear` rather than a flag on it (KTD17): `linear` means an agent SESSION
 * delegated the work, while `linear-label` means a human put the
 * `factory:eligible` label on an issue and the tick picked it up. They have
 * different `sourceRef` shapes — an agent-session id versus `<issueUuid>/<n>`,
 * where `n` is one more than the count of prior runs for that issue — so
 * collapsing them would make `@@unique([source, sourceRef])` compare two
 * different kinds of string, and a re-run of an issue would collide with its
 * own first attempt.
 *
 * `factory-prompt` is the factory's OTHER intake: an operator handed it a
 * prompt with no ticket (phase runner plan, KD5). Its `sourceRef` is the
 * caller's request id, and assessment files the Linear ticket the run is then
 * anchored on, so from dispatch onward it is an ordinary ticketed run.
 */
export const AGENT_RUN_SOURCES = [
  'linear',
  'granola',
  'slack',
  'granola-sweep',
  'manual',
  'agent',
  'linear-label',
  'factory-prompt'
] as const

export const AgentRunSourceSchema = z.enum(AGENT_RUN_SOURCES)
export type AgentRunSource = z.infer<typeof AgentRunSourceSchema>

export function isAgentRunSource(value: string): value is AgentRunSource {
  return (AGENT_RUN_SOURCES as readonly string[]).includes(value)
}

// =============================================================================
// Executors
// =============================================================================

/**
 * Where the run actually executes, once assessment decides. `local` is the
 * Mac mini poller + `dispatch.sh`; `agent-runner` is the pushed container;
 * `inline` is in-process and refused outside development.
 *
 * `vm` is the factory's own: the BACKEND names, creates, starts and destroys
 * an exe.dev VM for the run and hands it a per-run token, so no personal
 * machine is on the path (R8). It is the one executor whose `executorRef` is
 * written exclusively by the backend — a `started` event's
 * `data.executorRef` is IGNORED for a `vm` run (KTD18), because an agent that
 * could name its own VM could name someone else's for teardown.
 *
 * `launcher` is the factory launcher's executor (factory operator plan KTD4;
 * the launcher is removed, its rows remain): a phase-runner run the launcher
 * created, claimed and supervised itself, on the Mac or on a VM it provisioned. It is NOT `local`,
 * which belongs to `dispatch.sh` rows that can wait on a human
 * indefinitely. Every backend predicate written for launcher runs (the orphan
 * sweep, `release`, cancelling a parked run, `close`) is scoped to this value
 * alone, and the tick's dispatcher never claims it. Where the run actually
 * executes goes in `executorRef`.
 */
export const AGENT_RUN_EXECUTORS = ['local', 'agent-runner', 'inline', 'vm', 'launcher'] as const

export const AgentRunExecutorSchema = z.enum(AGENT_RUN_EXECUTORS)
export type AgentRunExecutor = z.infer<typeof AgentRunExecutorSchema>

export function isAgentRunExecutor(value: string): value is AgentRunExecutor {
  return (AGENT_RUN_EXECUTORS as readonly string[]).includes(value)
}

// =============================================================================
// Failure kinds
// =============================================================================

/**
 * WHY a run failed — and therefore whether it may retry (KTD5).
 *
 * THE STATUS ENUM GAINS NO VALUE FOR ANY OF THESE. A cancel is `FAILED` plus
 * `canceled`, a budget kill is `FAILED` plus `budget`. Putting them in the
 * status enum would cost a PlanetScale promotion per new way of failing and
 * would make every `status === 'FAILED'` reader wrong; putting them here keeps
 * "did it fail" and "why" as separate questions.
 *
 * Only `stall` and `executor` are retryable — see {@link isRetryableFailure}.
 *
 * - `stall`    — no heartbeat, no agent output, or past the lane's max run time (R13)
 * - `executor` — the VM or the transport broke under a run that had started
 * - `agent`    — the session itself ended badly; retrying replays the same reasoning
 * - `budget`   — cost passed `budgetUsd`; never retried without a human (R15)
 * - `canceled` — a human, a label removal, or the kill switch stopped it (R17, R18, R22)
 * - `gate`     — a hard stop was violated, e.g. a schema change merged without a
 *                matching approval (R25, KTD16)
 *
 * A start failure BEFORE any VM exists is deliberately not in this list: it is
 * not a failure at all. The run returns to `QUEUED` without spending an
 * attempt, so an exe.dev outage holds the queue instead of exhausting it
 * (KTD5).
 */
export const AGENT_RUN_FAILURE_KINDS = ['stall', 'executor', 'agent', 'budget', 'canceled', 'gate'] as const

export const AgentRunFailureKindSchema = z.enum(AGENT_RUN_FAILURE_KINDS)
export type AgentRunFailureKind = z.infer<typeof AgentRunFailureKindSchema>

export function isAgentRunFailureKind(value: string): value is AgentRunFailureKind {
  return (AGENT_RUN_FAILURE_KINDS as readonly string[]).includes(value)
}

/**
 * The two retryable kinds, as a predicate rather than an exported list.
 *
 * Callers ask the question instead of re-deriving the membership test against
 * their own copy of the set — the same discipline `needsHuman` uses on the web
 * side. A third copy of "which kinds retry" is the place this rule silently
 * drifts, and the cost of drifting the wrong way is a budget-killed run
 * retrying until it exhausts its attempts.
 */
const RETRYABLE_FAILURE_KINDS: readonly AgentRunFailureKind[] = ['stall', 'executor']

export function isRetryableFailure(kind: AgentRunFailureKind): boolean {
  return RETRYABLE_FAILURE_KINDS.includes(kind)
}

// =============================================================================
// Trigger / Mode / Status — mirror the Prisma enums verbatim
// =============================================================================

/** Who initiated the run — `source` already carries which event; this answers only human-vs-event. */
export const AgentRunTriggerSchema = z.enum(['HUMAN', 'AGENT', 'WEBHOOK'])
export type AgentRunTrigger = z.infer<typeof AgentRunTriggerSchema>

/** Whether a human is expected to answer a parked run. */
export const AgentRunModeSchema = z.enum(['INTERACTIVE', 'AUTONOMOUS'])
export type AgentRunMode = z.infer<typeof AgentRunModeSchema>

/**
 * The run lifecycle. See {@link AGENT_RUN_STATUS_TRANSITIONS} for the full
 * event -> transition table U2 enforces.
 *
 *   ASSESSING -> DECLINED
 *             -> PARKED    (ask-human at assessment)
 *             -> QUEUED    -> RUNNING -> PARKED  <-> RUNNING
 *                                     -> BLOCKED -> RUNNING | FAILED
 *                                     -> DONE | FAILED
 */
export const AgentRunStatusSchema = z.enum([
  'ASSESSING',
  'DECLINED',
  'PARKED',
  'QUEUED',
  'RUNNING',
  'BLOCKED',
  'DONE',
  'FAILED'
])
export type AgentRunStatus = z.infer<typeof AgentRunStatusSchema>

// =============================================================================
// Event types
// =============================================================================

/**
 * What a run reports over `POST /internal/agent-runs/:publicId/events`
 * (U2), in two families.
 *
 * LIFECYCLE (below) is where the run IS: every value but `retro` and `note`
 * moves `status`, per {@link AGENT_RUN_STATUS_TRANSITIONS}. `claim` is
 * deliberately absent: it is a route (`POST .../claim`), not a reported
 * event, because it is the one transition that mints the report token rather
 * than being reported BY it.
 *
 * OUTCOME ({@link AGENT_RUN_OUTCOME_EVENT_TYPES}) is what the run PRODUCED.
 * None of them appears in the transition table, because none of them moves
 * status — a run that files four tickets is still `RUNNING`.
 *
 * The lifecycle list below is module-private: the split matters to this
 * file's own composition, not to callers, who reach for
 * {@link AGENT_RUN_EVENT_TYPES} (both families) or
 * {@link AGENT_RUN_OUTCOME_EVENT_TYPES} (one of them).
 *
 * `gate1` is the Stage-1 triage verdict — the lane's `VERDICT:` line, whose
 * `data` carries `verdict` and `confidence` (`high` / `med` / `low`, the values
 * the lane's own verdict line emits) plus a per-lane third field (`severity` on
 * the defect lane, `size` on the improvement lane). It is never terminal: it holds the
 * run at `RUNNING` -> `RUNNING`. On a lane that stops at Gate 1 the very next
 * event is the `done` or `parked` carrying the outcome; on a lane that continues
 * it is followed by `gate2` and possibly `blocked` first, stages before any
 * terminal event. Either way `gate1` records the verdict, never the outcome.
 *
 * There is deliberately no `dispatched` event — and NOT because one is
 * impossible or because no report token exists yet. `run-ledger create`
 * claims in the same call and hands `RUN_ID` / `RUN_TOKEN` back before the
 * launch, and `dispatch.sh` already emits a post-create `failed` from a trap
 * armed before the VM starts, using exactly those variables. A `dispatched`
 * row is therefore mechanically available at that point; it is simply not
 * worth a row, because it would duplicate what `failed` and `started` already
 * carry. Launch failure is `failed`, launch success is the executor's
 * `started`, and a `dispatched` sitting between them is a third row no reader
 * branches on. The dispatch FACTS — the reply-to address (`sessionBridge`)
 * and the surface (`executor`) — are state rather than an event, so run
 * CREATION carries them instead.
 */
const AGENT_RUN_LIFECYCLE_EVENT_TYPES = [
  'started',
  'gate1',
  'gate2',
  'parked',
  'resumed',
  'blocked',
  'done',
  'failed',
  'retro',
  'note'
] as const

/**
 * The CONCRETE THINGS a run produced, one event per outcome.
 *
 * These are not lifecycle: none of them moves `status`, and none of them is
 * the run's headline. A run's `outcomes` JSON already holds the same facts,
 * but as one opaque blob per run — you cannot ask "every Linear issue this
 * lane filed this week" of a JSON column without reading every run and
 * parsing it. Each of these lands as its own `OperationsEvent` row instead,
 * so the ledger answers that question with a `WHERE event_type = …`.
 *
 * ⚠️ THEY MUST NOT WRITE `AgentRun.summary`. The lifecycle path
 * (`recordNonMutatingEvent` in the backend ledger) writes `params.summary`
 * onto the run's own headline, which is right for `note` and `retro` and
 * wrong here: eleven outcome events per run would leave the run's headline
 * reading "created SCH-1234" instead of "done — 21 finding(s) processed".
 * The backend ledger routes these to a separate writer for exactly that
 * reason; see `services/agent-runs/ledger.ts`.
 *
 * Payload shapes: one Zod schema per type, in the sibling
 * `agent-run-outcome-events` module.
 */
export const AGENT_RUN_OUTCOME_EVENT_TYPES = [
  'findings.extracted',
  'school.resolved',
  'school.unresolved',
  'linear.issue_created',
  'linear.comment_attached',
  'linear.need_created',
  'linear.write_failed',
  'hubspot.meeting_created',
  'hubspot.meeting_skipped',
  'hubspot.meeting_failed',
  'dispatch.intent_recorded',
  // The factory's own (phase runner plan): a classification verdict, and one
  // phase step's start or done edge. Stored as `run.classified` / `run.phase`.
  'classified',
  'phase'
] as const

export const AgentRunOutcomeEventTypeSchema = z.enum(AGENT_RUN_OUTCOME_EVENT_TYPES)
export type AgentRunOutcomeEventType = z.infer<typeof AgentRunOutcomeEventTypeSchema>

export function isAgentRunOutcomeEventType(value: string): value is AgentRunOutcomeEventType {
  return (AGENT_RUN_OUTCOME_EVENT_TYPES as readonly string[]).includes(value)
}

export const AGENT_RUN_EVENT_TYPES = [...AGENT_RUN_LIFECYCLE_EVENT_TYPES, ...AGENT_RUN_OUTCOME_EVENT_TYPES] as const

type AgentRunLifecycleEventType = (typeof AGENT_RUN_LIFECYCLE_EVENT_TYPES)[number]

export const AgentRunEventTypeSchema = z.enum(AGENT_RUN_EVENT_TYPES)
export type AgentRunEventType = z.infer<typeof AgentRunEventTypeSchema>

export function isAgentRunEventType(value: string): value is AgentRunEventType {
  return (AGENT_RUN_EVENT_TYPES as readonly string[]).includes(value)
}

// =============================================================================
// Status transition table
// =============================================================================

/**
 * One row per valid `(fromStatus, eventType) -> toStatus` transition. This is
 * DOCUMENTATION plus a place U2's tests and this package's own test assert
 * "every `AgentRunStatus` value appears somewhere" — the real transition
 * guard is enforced in SQL by U2's conditional `UPDATE`, not by reading this
 * array at runtime. Two entries name something other than a member of
 * {@link AGENT_RUN_EVENT_TYPES}: `claim` is a route
 * (`POST .../claim`), and `assessed` is the assessment job's own write
 * (`ASSESSING` -> `DECLINED` / `QUEUED` / `PARKED`) — neither is reported
 * through `POST .../events`, but both are real transitions the state machine
 * makes and belong in the same table as every other one.
 *
 * FIVE CONTROL TRANSITIONS ARE NOT REPORTED EVENTS (Phase 1, U1). Like
 * `claim` and `assessed` above, they are writes the control plane makes rather
 * than something a run says about itself, and they are here because they are
 * real transitions of the same state machine:
 *
 * - `retry`    — the tick requeues a retryable failure with backoff (R14). It
 *                reuses THIS row (KTD4), which is why the destination is
 *                `QUEUED` and not a new run. FROM `FAILED` ONLY: retry
 *                eligibility is decided by `failureKind` (KTD5), and a run that
 *                has not failed has no `failureKind` to decide on. The tick
 *                kills a stalled run — writing `FAILED` + `stall` — and only
 *                then requeues it, so the failure is always recorded before the
 *                next attempt. A `RUNNING -> QUEUED` shortcut would lose that
 *                row and let a run retry with no evidence of why.
 * - `resume`   — an operator (or an allowed Linear commenter, R23) resumes a
 *                parked run. `PARKED -> QUEUED`, or `PARKED -> ASSESSING` when
 *                the run parked mid-assessment (KTD6). A resumed run needs a
 *                slot like any other, which is the whole reason it goes back
 *                through the queue rather than straight to `RUNNING`.
 * - `unclaim`  — the dispatcher hands a claim back. Two causes, one
 *                transition: the post-claim recount found a cap exceeded
 *                (KTD8), or the VM could not be created at all, which spends
 *                no attempt (KTD5).
 * - `cancel`   — `FAILED` with `failureKind: 'canceled'`, from anywhere
 *                non-terminal (R17, R22). A cancelled run is never retried.
 * - `landed`   — the GitHub landing check, and ONLY it, moves a run to `DONE`
 *                (KTD16). The run's own `done` event no longer owns that
 *                destination for a `vm` run; the check confirms the PR merged.
 *
 * `resumed` ACCEPTS BOTH `PARKED` AND `RUNNING`, AND BOTH ARE LOAD-BEARING
 * (U3). KTD6 retires `resumed` as the RESUME transition — that job belongs to
 * the `resume` row above, which puts a parked run back in the queue where it
 * competes for a slot like any other. What is left for `resumed` under the
 * factory is a report, not a move: the VM runner posts it once the session is
 * actually back up, by which time `resume` and the dispatcher's claim have
 * already walked the row `PARKED -> QUEUED -> RUNNING`, so the event lands
 * `RUNNING -> RUNNING`.
 *
 * `PARKED -> RUNNING` STAYS, because it is the shipped exit from `PARKED` for
 * every `local` lane running today: a human answers in the session and the
 * agent's first act is `run-ledger event resumed`, with no control plane
 * anywhere in that path to have issued a `resume` first. U1 deliberately left
 * this row alone rather than narrowing it to the factory's half, because
 * narrowing it alone would 409 every live resume while the CLI still exited 0
 * — a silently broken lane, the exact failure
 * `status-transitions-mirror.test.ts` exists to catch. U3 widens it in the same
 * diff as the backend mirror and the ledger rules in
 * `tooling/coordinator/README.md`, which may not call `resumed`
 * "the ONLY exit from PARKED" — the control plane's `resume` is now another.
 *
 * `ASSESSING` LEAVES `resumed`'s DESTINATIONS. An assessment park is resumed by
 * the control plane (`resume`, `PARKED -> ASSESSING`), which re-runs the
 * assessment; a run has no way to report itself back into assessment, and
 * leaving the destination here would suggest it did.
 *
 * `failed` is unchanged for the same reason: `PARKED -> FAILED` arrives as the
 * `cancel` row above, which a control plane writes, rather than by widening an
 * event row the backend mirror would then disagree with.
 *
 * {@link AGENT_RUN_OUTCOME_EVENT_TYPES} is absent by construction: `event` is
 * typed to the LIFECYCLE family, so listing an outcome type here is a compile
 * error rather than a transition nothing enforces.
 */
export const AGENT_RUN_STATUS_TRANSITIONS: ReadonlyArray<{
  event: 'claim' | 'assessed' | 'retry' | 'resume' | 'unclaim' | 'cancel' | 'landed' | AgentRunLifecycleEventType
  from: readonly AgentRunStatus[]
  to: readonly AgentRunStatus[]
}> = [
  { event: 'assessed', from: ['ASSESSING'], to: ['DECLINED', 'QUEUED', 'PARKED'] },
  { event: 'claim', from: ['QUEUED'], to: ['RUNNING'] },
  { event: 'started', from: ['RUNNING'], to: ['RUNNING'] },
  { event: 'gate1', from: ['RUNNING'], to: ['RUNNING'] },
  { event: 'gate2', from: ['RUNNING'], to: ['RUNNING'] },
  { event: 'parked', from: ['ASSESSING', 'RUNNING', 'BLOCKED', 'PARKED'], to: ['PARKED'] },
  { event: 'resumed', from: ['PARKED', 'RUNNING'], to: ['RUNNING'] },
  { event: 'blocked', from: ['RUNNING'], to: ['BLOCKED'] },
  { event: 'done', from: ['RUNNING', 'BLOCKED'], to: ['DONE'] },
  { event: 'failed', from: ['QUEUED', 'RUNNING', 'BLOCKED'], to: ['FAILED'] },
  // Control-plane transitions (Phase 1) — see the five bullets above.
  { event: 'retry', from: ['FAILED'], to: ['QUEUED'] },
  { event: 'resume', from: ['PARKED'], to: ['QUEUED', 'ASSESSING'] },
  { event: 'unclaim', from: ['RUNNING'], to: ['QUEUED'] },
  { event: 'cancel', from: ['ASSESSING', 'QUEUED', 'RUNNING', 'BLOCKED', 'PARKED'], to: ['FAILED'] },
  { event: 'landed', from: ['QUEUED', 'RUNNING', 'BLOCKED', 'PARKED'], to: ['DONE'] },
  // `retro` / `note` never change status — listed for completeness. `from`
  // and `to` are identical (every status, pairwise unchanged), not a
  // cross-product of "any status to any other status".
  { event: 'retro', from: AgentRunStatusSchema.options, to: AgentRunStatusSchema.options },
  { event: 'note', from: AgentRunStatusSchema.options, to: AgentRunStatusSchema.options }
]

// =============================================================================
// Wire shape
// =============================================================================

/** Loose JSON — shapes not yet built are not over-specified here. */
const looseJson = z.record(z.string(), z.unknown()).nullable()

/**
 * One agent run, as an operator or the `sj` CLI sees it.
 */
export const AgentRunSchema = z.object({
  /** `AgentRun.publicId`. */
  id: z.string(),
  lane: AgentRunLaneSchema,
  source: AgentRunSourceSchema,
  sourceRef: z.string().nullable(),
  trigger: AgentRunTriggerSchema,
  mode: AgentRunModeSchema,
  status: AgentRunStatusSchema,
  executor: AgentRunExecutorSchema.nullable(),
  executorRef: z.string().nullable(),
  sessionBridge: z.string().nullable(),
  /** The parent run's PUBLIC id, never the internal `BigInt`. */
  parentRunId: z.string().nullable(),
  linearIssueKey: z.string().nullable(),
  classification: z.string().nullable(),
  classOverride: z.string().nullable(),
  verdict: z.string().nullable(),
  fingerprint: z.string().nullable(),
  assessment: looseJson,
  brief: looseJson,
  outcomes: looseJson,
  retro: looseJson,
  summary: z.string().nullable(),
  declineReason: z.string().nullable(),
  parkQuestion: z.string().nullable(),
  costUsd: z.number().nullable(),
  inputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  createdAt: z.string().datetime(),
  assessedAt: z.string().datetime().nullable(),
  startedAt: z.string().datetime().nullable(),
  parkedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  lastEventAt: z.string().datetime().nullable(),

  // ── Factory control loop (Phase 1, U1) ────────────────────────────────────
  // Every field below has a default or is nullable, so a run created before
  // the factory existed still parses. `reportTokenHash` remains absent from
  // this shape and MUST stay absent — see the file header.

  /** Current attempt. A retry reuses the row rather than inserting a second one (KTD4). */
  attempt: z.number().int().min(1).default(1),
  /** When a requeued run becomes eligible again — `min(10s * 2^(attempt-1), 5min)` (R14). */
  nextAttemptAt: z.string().datetime().nullable(),
  failureKind: AgentRunFailureKindSchema.nullable(),
  lastError: z.string().nullable(),
  /**
   * Linear priority, copied at intake so the dispatcher sorts without a Linear
   * read (R6). 1 Urgent, 2 High, 3 Medium, 4 Low — never null and never 0,
   * because MySQL sorts NULL first and Linear's own "No priority" is 0, either
   * of which would rank an unprioritized run ahead of every urgent one.
   */
  priority: z.number().int().min(1).max(4).default(3),
  /** The run's ceiling in dollars, frozen at dispatch so a config change never kills a live run (R15). */
  budgetUsd: z.number().nullable(),
  /**
   * VM lifecycle. `vmDestroyedAt === null` with `executorRef` set means the VM
   * is still live and still counts against the `vm` cap, whatever the run's
   * status is (KTD8) — parked and grace-period VMs included.
   */
  vmCreatedAt: z.string().datetime().nullable(),
  vmDestroyedAt: z.string().datetime().nullable(),
  /** Last heartbeat from the runner (60s cadence) — the "is the VM alive" signal (KTD14). */
  heartbeatAt: z.string().datetime().nullable(),
  /** Last stream-json event from the session — the "is the AGENT alive" signal (KTD14). */
  agentOutputAt: z.string().datetime().nullable(),
  /** Set when a cancel is REQUESTED, which is before the VM is actually gone. */
  cancelRequestedAt: z.string().datetime().nullable(),
  /** The Claude session id on the VM, so a resume can continue it in place (KTD6). */
  sessionId: z.string().nullable(),
  /** The issue UUID the Linear API takes — distinct from `linearIssueKey`, which humans read. */
  linearIssueId: z.string().nullable(),
  /** The ONE "Factory workpad" comment this run keeps current (R20). */
  workpadCommentId: z.string().nullable(),
  /** Status+phase last successfully written to the ticket — level-triggered sync (KTD20). */
  linearSyncedState: z.string().nullable(),
  /** Found by the landing check through GitHub, never taken from a VM report (KTD16). */
  prUrl: z.string().nullable(),
  /** The branch the run is working, so attempt 2+ continues it instead of opening a second PR (KTD4). */
  prBranch: z.string().nullable(),
  /** sha256 of `prisma/schema/**` at the approved PR head — an approval cannot outlive its diff (R25). */
  schemaApprovalDigest: z.string().nullable(),
  schemaApprovedAt: z.string().datetime().nullable()
})
export type AgentRun = z.infer<typeof AgentRunSchema>
